import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import type { JobStatus } from '../shared/api';
import { STATUS_LABELS, TONE_BADGE, type Tone } from '../lib/format';

/** The small, repeated pieces of chrome. Everything here is presentational. */

export function Card({ children, className = '', title, action }: { children: ReactNode; className?: string; title?: ReactNode; action?: ReactNode }) {
  return (
    <section className={`rounded-xl border border-border bg-surface ${className}`}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <h2 className="text-sm font-semibold tracking-wide text-ink">{title}</h2>
          {action}
        </header>
      )}
      <div className={title || action ? 'p-5' : 'p-5'}>{children}</div>
    </section>
  );
}

export function Pill({ tone = 'none', children, className = '' }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONE_BADGE[tone]} ${className}`}>
      {children}
    </span>
  );
}

const STATUS_TONE: Record<JobStatus, Tone> = {
  new: 'none',
  property_verified: 'watch',
  // Prepared but not mailed: the 45-day clock is still running and the
  // contractor still has to walk it to the post office.
  noo_prepared: 'watch',
  noo_sent: 'watch',
  noo_delivered: 'ok',
  active: 'ok',
  invoiced: 'watch',
  partially_paid: 'watch',
  dispute: 'urgent',
  litigation: 'urgent',
  closed: 'none',
};

export function StatusBadge({ status }: { status: JobStatus }) {
  return <Pill tone={STATUS_TONE[status] ?? 'none'}>{STATUS_LABELS[status] ?? status}</Pill>;
}

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-tint text-white hover:bg-tint/85 border-transparent',
  secondary: 'bg-surface-alt text-ink hover:bg-border border-border',
  danger: 'bg-urgent text-white hover:bg-urgent/85 border-transparent',
  ghost: 'bg-transparent text-muted hover:text-ink hover:bg-surface-alt border-transparent',
};

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${VARIANTS[variant]} ${className}`}
    />
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-dim">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </label>
  );
}

const CONTROL =
  'w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-ink outline-none placeholder:text-dim focus:border-tint';

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CONTROL} ${className}`} />;
}

export function Select({ className = '', children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={`${CONTROL} ${className}`}>
      {children}
    </select>
  );
}

export function Textarea({ className = '', ...props }: InputHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${CONTROL} min-h-20 ${className}`} />;
}

export function Banner({ tone = 'urgent', title, children }: { tone?: Tone; title?: ReactNode; children?: ReactNode }) {
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${TONE_BADGE[tone]}`} role="status">
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className="mt-0.5 opacity-90">{children}</div>}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-10 text-center text-sm text-dim">{children}</p>;
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-sm text-muted" role="status">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-tint" aria-hidden />
      {label}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: ReactNode; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/65 p-6" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
      <div className={`my-auto w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} rounded-xl border border-border bg-surface shadow-2xl`}>
        <header className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function DataTable({ head, children, empty }: { head: ReactNode[]; children: ReactNode; empty?: ReactNode }) {
  const rows = Array.isArray(children) ? children.flat() : children;
  const isEmpty = Array.isArray(rows) ? rows.filter(Boolean).length === 0 : !rows;
  if (isEmpty && empty) return <Empty>{empty}</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[42rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            {head.map((h, i) => (
              <th key={i} className="whitespace-nowrap px-3 py-2 text-xs font-semibold uppercase tracking-wider text-dim">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

export function Td({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-3 py-2.5 align-middle text-ink ${className}`}>{children}</td>;
}

export function KeyValue({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="divide-y divide-border">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-start justify-between gap-6 py-2.5">
          <dt className="text-sm text-dim">{k}</dt>
          <dd className="text-right text-sm font-medium text-ink">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A file picker that shows what was chosen. Deliberately a plain <input> under
 * a styled label rather than a drag-and-drop zone: contractors use this on a
 * phone, where the native picker is the camera roll and dragging means nothing.
 */
export function FilePicker({
  label,
  accept,
  file,
  onPick,
  hint,
  disabled,
}: {
  label: string;
  accept?: string;
  file: File | null;
  onPick: (file: File | null) => void;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <Field label={label} hint={hint}>
      <div className="flex flex-wrap items-center gap-3">
        <label
          className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-surface-alt px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-border ${
            disabled ? 'pointer-events-none opacity-45' : ''
          }`}
        >
          {file ? 'Choose a different file' : 'Choose file'}
          <input
            type="file"
            accept={accept}
            disabled={disabled}
            className="sr-only"
            onChange={(e) => onPick(e.target.files?.[0] ?? null)}
          />
        </label>
        <span className="text-sm text-muted">{file ? file.name : 'No file chosen'}</span>
      </div>
    </Field>
  );
}

/**
 * One choice in a two-path fork (platform vs. self-service). A disabled option
 * still renders, because seeing the path that is coming is the point.
 */
export function ChoiceCard({
  title,
  description,
  badge,
  selected,
  disabled,
  onSelect,
}: {
  title: ReactNode;
  description: ReactNode;
  badge?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={!!selected}
      onClick={onSelect}
      className={`flex-1 rounded-xl border p-4 text-left transition-colors ${
        selected ? 'border-tint bg-surface-alt' : 'border-border bg-surface hover:border-tint/60'
      } disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:border-border`}
    >
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-ink">{title}</span>
        {badge}
      </span>
      <span className="mt-1.5 block text-sm text-muted">{description}</span>
    </button>
  );
}

/** Numbered progress rail. Shows where the contractor is in a multi-step flow. */
export function Steps({ steps, current }: { steps: string[]; current: number }) {
  return (
    <ol className="flex flex-wrap gap-2" aria-label="Progress">
      {steps.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'now' : 'todo';
        return (
          <li
            key={label}
            aria-current={state === 'now' ? 'step' : undefined}
            className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${
              state === 'done'
                ? TONE_BADGE.ok
                : state === 'now'
                  ? TONE_BADGE.watch
                  : 'border-border bg-surface text-dim'
            }`}
          >
            <span aria-hidden>{state === 'done' ? '✓' : i + 1}</span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}
